import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { bookingsDuringLeave, leaveCapMessage, scheduleDayOff, removeDayOff } from '@/domain/artistFlow';
import { mostRecentUncompensatedDoubleDay } from '@/domain/workload';
import { buildDayOffBlock, resolveDayOff, type DayOffKind } from '@/domain/artistAdmin';

interface Props {
  artistId: string;
  /** yyyy-MM-dd of the clicked day. */
  date: string;
  onClose: () => void;
}

const KIND_OPTIONS: Array<{ value: DayOffKind; label: string; hint: string }> = [
  { value: 'rdo', label: 'RDO', hint: 'Rostered day off for this date' },
  { value: 'comp', label: 'Comp day', hint: 'Granted for working a double day (note required)' },
  { value: 'leave', label: 'Leave', hint: 'Annual / personal leave' },
];

export default function DayOffModal({ artistId, date, onClose }: Props) {
  const artist = useBrainStore((s) => s.artists[artistId]);
  const bookings = useBrainStore((s) => s.bookings);
  const clockIso = useSimulationStore((s) => s.clockIso);

  const [kind, setKind] = useState<DayOffKind>('rdo');
  const suggestedCompDate = useMemo(
    () => (artist ? mostRecentUncompensatedDoubleDay(artist, Object.values(bookings), clockIso) : null),
    [artist, bookings, clockIso],
  );
  const defaultCompNote = suggestedCompDate
    ? `Comp for double day on ${format(new Date(`${suggestedCompDate}T00:00:00`), 'd MMM')}`
    : '';
  const [note, setNote] = useState('');
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');

  if (!artist) return null;

  const dayOff = resolveDayOff(artist.availability, new Date(`${date}T00:00:00`));

  const onRemove = async () => {
    setRemoving(true);
    setError('');
    if (await removeDayOff(artistId, date)) onClose();
    else setError('The day off could not be removed. Please try again.');
    setRemoving(false);
  };

  const effectiveNote = kind === 'comp' && !note ? defaultCompNote : note;
  const compInvalid = kind === 'comp' && !effectiveNote.trim();
  const dateLabel = format(new Date(`${date}T00:00:00`), 'EEE d MMM yyyy');

  // D10-02: leave never cancels a booking, so name the ones on this day.
  const clashes = bookingsDuringLeave(artistId, { startDate: date, endDate: date }, bookings);

  const onSubmit = () => {
    if (compInvalid) return;
    const input = {
      kind,
      date,
      note: effectiveNote.trim() || undefined,
      compensatesDate: kind === 'comp' ? suggestedCompDate ?? undefined : undefined,
    };
    // D10-02: leave is capped by the annual allowance (working days).
    const overCap = leaveCapMessage(artist, buildDayOffBlock(input, 'cap-check'));
    if (overCap) {
      setError(overCap);
      return;
    }
    scheduleDayOff(artistId, input);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay/80 px-6 backdrop-blur-sm"
      onClick={removing ? undefined : onClose}
    >
      <div
        data-testid="day-off-modal"
        className="w-full max-w-md rounded-xl border border-gold-dim/40 bg-ink-deep p-6 text-cream-soft"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-cream">Day off — {artist.displayName}</h2>
        <p className="mt-1 text-sm text-cream-faint">{dateLabel}</p>

        <div className="mt-4 space-y-2">
          {KIND_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className="flex cursor-pointer items-start gap-3 rounded-lg border border-cream-faint/15 p-3 hover:border-cream-faint/40"
            >
              <input
                type="radio"
                name="day-off-kind"
                className="mt-1"
                checked={kind === opt.value}
                onChange={() => setKind(opt.value)}
                data-testid={`day-off-kind-${opt.value}`}
              />
              <span>
                <span className="block text-sm font-medium text-cream">{opt.label}</span>
                <span className="block text-xs text-cream-faint">{opt.hint}</span>
              </span>
            </label>
          ))}
        </div>

        <label className="mt-4 block text-xs uppercase tracking-wide text-cream-faint">
          Note{kind === 'comp' ? ' (required)' : ' (optional)'}
          <textarea
            data-testid="day-off-note"
            className="mt-1 w-full rounded-lg border border-cream-faint/20 bg-ink-black/40 p-2 text-sm text-cream"
            rows={2}
            value={note || (kind === 'comp' ? defaultCompNote : '')}
            onChange={(e) => setNote(e.target.value)}
            placeholder={kind === 'comp' ? 'Reason / which double day this compensates' : 'Optional note'}
          />
        </label>

        {clashes.length > 0 && !dayOff ? (
          <p className="mt-3 text-sm text-orange" data-testid="day-off-bookings-warning">
            {clashes.length} booking{clashes.length === 1 ? ' is' : 's are'} on this day. A day off doesn&apos;t cancel{' '}
            {clashes.length === 1 ? 'it' : 'them'}: move {clashes.length === 1 ? 'it' : 'them'} to another day or artist.
          </p>
        ) : null}
        {error && <p role="alert" className="mt-3 text-sm text-blood">{error}</p>}
        {dayOff?.source === 'pattern' && <p className="mt-3 text-sm text-cream-faint">This is a weekly roster day off. Change the weekly roster in the artist profile to make it a working day.</p>}
        <div className="mt-5 flex justify-end gap-2">
          {dayOff?.source === 'block' && (
            <button data-testid="day-off-remove" disabled={removing} onClick={() => void onRemove()} className="mr-auto rounded-lg px-3 py-1.5 text-sm text-blood disabled:opacity-40">
              {removing ? 'Removing…' : 'Remove day off'}
            </button>
          )}
          <button className="rounded-lg px-3 py-1.5 text-sm text-cream-faint hover:text-cream" onClick={removing ? undefined : onClose}>
            Cancel
          </button>
          <button
            data-testid="day-off-submit"
            className="rounded-lg bg-gold px-4 py-1.5 text-sm font-semibold text-ink-black disabled:opacity-40"
            disabled={compInvalid || removing}
            onClick={onSubmit}
          >
            Save day off
          </button>
        </div>
      </div>
    </div>
  );
}
